import { supabase } from '@/integrations/supabase/client';

export interface VenueRating {
  average: number;
  count: number;
}

export const venueRatingsService = {
  /**
   * Get average rating and count for a venue
   */
  async getVenueRating(venueId: string): Promise<VenueRating | null> {
    try {
      const { data, error } = await supabase
        .from('venue_customer_ratings')
        .select('rating')
        .eq('venue_id', venueId);

      if (error) {
        console.error('Error loading venue rating:', error);
        return null;
      }

      if (!data || data.length === 0) {
        return null;
      }

      const average = data.reduce((sum, item) => sum + item.rating, 0) / data.length;
      return {
        average: Math.round(average * 10) / 10,
        count: data.length
      };
    } catch (error) {
      console.error('Error loading venue rating:', error);
      return null;
    }
  },

  /**
   * Get ratings for multiple venues in batch
   */
  async getVenueRatings(venueIds: string[]): Promise<Record<string, VenueRating>> {
    try {
      const { data, error } = await supabase
        .from('venue_customer_ratings')
        .select('venue_id, rating')
        .in('venue_id', venueIds);

      if (error) {
        console.error('Error loading venue ratings:', error);
        return {};
      }

      const ratingsMap: Record<string, VenueRating> = {};
      
      // Group ratings by venue_id
      const groupedRatings = data.reduce((acc, rating) => {
        if (!acc[rating.venue_id]) {
          acc[rating.venue_id] = [];
        }
        acc[rating.venue_id].push(rating.rating);
        return acc;
      }, {} as Record<string, number[]>);

      // Calculate average and count for each venue
      Object.entries(groupedRatings).forEach(([venueId, ratings]) => {
        const average = ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length;
        ratingsMap[venueId] = {
          average: Math.round(average * 10) / 10,
          count: ratings.length
        };
      });

      return ratingsMap;
    } catch (error) {
      console.error('Error loading venue ratings:', error);
      return {};
    }
  }
};